import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Button, styled } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Question } from '../../types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { pdfs } from '../../services/api';

const PDFWrapper = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  width: '100%',
  maxWidth: '100%', // Allow full width
  margin: '0 auto',
  position: 'relative',
  overflow: 'visible', // Changed from 'hidden' to allow content to extend beyond container
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  '& embed, & object': {
    width: '100%',
    height: 'auto', // Height will be calculated dynamically
    display: 'block',
    border: 'none',
    margin: 0,
    padding: 0,
  }
}));

const CompletionIndicator = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme?.spacing(1),
  marginTop: theme?.spacing(2),
  color: theme?.palette.success.main,
}));

interface Props {
  question: Question;
  onValidationChange?: (isValid: boolean) => void;
  onComplete?: () => void;
  isSubmitting?: boolean;
}

export const PDFQuestion: React.FC<Props> = ({
  question,
  onValidationChange,
  onComplete,
  isSubmitting = false,
}) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [pdfHeight, setPdfHeight] = useState(600); // Default height
  const pdfRef = useRef<HTMLEmbedElement>(null);

  useEffect(() => {
    // Reset states when question changes
    setIsLoading(true);
    setHasError(false);
    setIsCompleted(false);
    setPdfUrl('');
    setErrorMessage('');
    setPdfHeight(600); // Reset to default height
    
    const fetchPdfUrl = async () => {
      console.log('[PDFQuestion] Fetching PDF URL for question:', question.id);
      try {
        if (!question.pdf_url) {
          console.error('[PDFQuestion] No PDF URL provided');
          setErrorMessage('No PDF URL provided');
          setHasError(true);
          setIsLoading(false);
          return;
        }

        let key = question.pdf_url;
        if (key.startsWith('http')) {
          const url = new URL(key);
          const pathParts = url.pathname.split('/');
          key = pathParts[pathParts.length - 1];
        }

        console.log('[PDFQuestion] Getting signed URL for key:', key);
        const signedUrl = await pdfs.getSignedUrl(key);
        setPdfUrl(signedUrl);
        setIsLoading(false);
        console.log('[PDFQuestion] PDF URL fetched successfully');
      } catch (error) {
        console.error('[PDFQuestion] Error fetching PDF URL:', error);
        setErrorMessage('Failed to load PDF');
        setHasError(true);
        setIsLoading(false);
      }
    };

    fetchPdfUrl();
  }, [question.id, question.pdf_url]);

  // Always validate as true once PDF is loaded
  useEffect(() => {
    if (onValidationChange) {
      const isValid = !isLoading && !hasError;
      console.log('[PDFQuestion] Validation state changed:', {
        questionId: question.id,
        isLoading,
        hasError,
        isValid
      });
      onValidationChange(isValid);
      
      // If PDF is loaded and valid, mark as completed
      if (isValid && !isCompleted) {
        console.log('[PDFQuestion] Marking as completed');
        setIsCompleted(true);
      }
    }
  }, [onValidationChange, isLoading, hasError, isCompleted, question.id]);

  // Handle PDF load event to adjust height based on content
  const handlePdfLoad = () => {
    if (pdfRef.current) {
      // A4 aspect ratio is approximately 1:1.414 (width:height)
      const containerWidth = pdfRef.current.clientWidth;
      const aspectRatio = 1.414; // A4 aspect ratio
      
      const calculatedHeight = containerWidth * aspectRatio ;
      
      // Set the height to accommodate the full PDF content
      setPdfHeight(calculatedHeight);
      console.log('[PDFQuestion] Set PDF height with reduced length:', calculatedHeight);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography color="error">{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PDFWrapper>
        <embed 
          ref={pdfRef}
          src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&zoom=page-width`}
          type="application/pdf"
          style={{ height: `${pdfHeight}px` }}
          onLoad={handlePdfLoad}
        />
      </PDFWrapper>
    </Box>
  );
};

export default PDFQuestion;
